/**
 * Statement of Applicability approval (ISO/IEC 27001:2022 Cl. 6.1.3(d); Workflow slide 10, Stage 6;
 * PRD s2.2 - the Lead Advisor approves). The API is the authority: this file mirrors its preconditions
 * only so the button can be disabled early, and every server refusal is surfaced verbatim.
 */
import { formatDdMmYyyy } from "./formatDdMmYyyy";
import { normalizeSoaApplicable } from "./soaApplicability";

export const SOA_REOPEN_MIN = 10;
export const SOA_REOPEN_MAX = 2000;
export const SOA_AI_CONFIDENCE_NOTE = "AI-generated draft - confidence not provided by the model";

/** Same rule as the server's `isSoaApproved`: the full stamp must be present. */
export function isSoaApproved(pkg) {
  return Boolean(pkg && pkg.approvalStatus === "approved" && pkg.approvedAt && pkg.approvedBy);
}

const blank = (s) => !String(s ?? "").trim();
const joinIds = (ids) => `${ids.slice(0, 20).join(", ")}${ids.length > 20 ? ` (+${ids.length - 20} more)` : ""}`;

/** Client-side preview of the server's `soaApprovalBlockers` (UX only; the server re-checks). */
export function soaApprovalBlockers(pkg) {
  const rows = Array.isArray(pkg?.rows) ? pkg.rows : [];
  if (rows.length === 0) return ["The Statement of Applicability has no rows. Sync it from the published treatment plan first."];
  const out = [];
  const label = (r) => r.controlIdDisplay || r.controlId;
  if (!pkg.lastAiDetailsAt) out.push("The justification / implementation narrative has not been generated yet. Run generate-details first.");
  const unset = rows.filter((r) => normalizeSoaApplicable(r.applicable) === "UNSET").map(label);
  if (unset.length) out.push(`Applicability (Y/N) is not decided for: ${joinIds(unset)}.`);
  const noJ = rows.filter((r) => blank(r.justification)).map(label);
  if (noJ.length) out.push(`Justification is empty for: ${joinIds(noJ)}.`);
  const noI = rows.filter((r) => blank(r.implementation)).map(label);
  if (noI.length) out.push(`Implementation status is empty for: ${joinIds(noI)}.`);
  return out;
}

/** null when valid, else the message to show under the reopen reason field. */
export function validateReopenReason(reason) {
  const n = String(reason ?? "").trim().length;
  if (n < SOA_REOPEN_MIN) return `Enter a reason of at least ${SOA_REOPEN_MIN} characters (${n} so far).`;
  if (n > SOA_REOPEN_MAX) return `The reason must be at most ${SOA_REOPEN_MAX} characters.`;
  return null;
}

/** "Approved by a@b.com on 21-10-2026 (version 2)". */
export function soaApprovedBannerText(pkg) {
  const who = pkg?.approvedByEmail || pkg?.approvedBy || "a Lead Advisor";
  const v = Number.isInteger(pkg?.approvalVersion) ? ` (version ${pkg.approvalVersion})` : "";
  return `Approved by ${who} on ${formatDdMmYyyy(pkg?.approvedAt)}${v}`;
}

/**
 * Non-blocking staleness warning (ISO/IEC 27001:2022 Cl. 6.1.3(d)): the treatment plan was edited after the SoA
 * was approved. Returns null unless the SoA is approved AND the server-written marker is present.
 */
export function soaPostApprovalEditsText(pkg) {
  const m = pkg?.postApprovalEdits;
  if (!isSoaApproved(pkg) || !m || !Number.isInteger(m.count) || m.count < 1) return null;
  const by = m.lastEditedBy?.name || m.lastEditedBy?.email || "an advisor";
  return `Treatment plan edited after SoA approval (${m.count} edit(s), last ${formatDdMmYyyy(m.lastEditedAt)} by ${by}) - the approved SoA may be out of date. Reopen the SoA to re-approve.`;
}

/**
 * Normalises a failed approve/reopen/sync/generate response into what the UI shows.
 * @returns {{ message: string, blockers: string[], reason: string|null }}
 */
export function parseSoaApprovalError(status, json) {
  const err = json?.error || {};
  const details = err.details && typeof err.details === "object" ? err.details : {};
  const blockers = Array.isArray(details.blockers) ? details.blockers.filter((b) => typeof b === "string" && b) : [];
  const reason = typeof details.reason === "string" ? details.reason : null;
  let message = typeof err.message === "string" && err.message ? err.message : "";
  if (!message) {
    if (reason === "SOA_APPROVED_LOCKED") message = "This Statement of Applicability is approved and locked. A Lead Advisor must reopen it for correction first.";
    else if (status === 403) message = "Only the Lead Advisor of this project can do this.";
    else message = `The request could not be completed (error ${status}).`;
  }
  return { message, blockers, reason };
}

/** True when a server refusal means the SoA is approved/locked (the package should be refreshed). */
export const isSoaLockedError = (parsed) => parsed?.reason === "SOA_APPROVED_LOCKED";

/** Fields the server owns; the client must never send them back in a profile PUT. */
const SERVER_OWNED = [
  "approvalStatus", "approvedAt", "approvedBy", "approvedByEmail", "approvedByRole", "approvalVersion",
  "approvedContentHash", "reopenedAt", "reopenReason", "versionHistory", "lastAiDetailsProvenance", "postApprovalEdits",
];
export function stripSoaServerOwnedFields(pkg) {
  const out = { ...(pkg || {}) };
  for (const k of SERVER_OWNED) delete out[k];
  return out;
}
